import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
  success: '#10b981',
  danger: '#ef4444',
};

const VerifyAuditScreen = () => {
  const { t } = useTranslation();
  const [leafHash, setLeafHash] = useState('');
  const [merklePath, setMerklePath] = useState('');
  const [rootHash, setRootHash] = useState('');
  const [result, setResult] = useState(null); // { valid, computedRoot, reason }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exampleExpanded, setExampleExpanded] = useState(false);

  // Hardcoded real proof from the April 7, 2026 anchor (tenant 69cc34ba, 85 leaves)
  const example = {
    date: '2026-04-07',
    leafHash: '0x0826ac87861e4732b3ea79d736ecd3c8d76f0b711d44614f9bbf0121575654c7',
    rootHash: '0xd8bb80e13c3c0a3fb45b5363f903c1946771480704b394e73a62ed4b85753402',
    merklePath: JSON.stringify([
      { hash: '0x5160603094ca4f68bbd3c512b78fed709c41712e9f295e75326eb7c8566fbfaf', position: 'right' },
      { hash: '0xf0ac9a272d0207987a83d9f4999d6f97272e18316d82622d19c9e71adfd88ece', position: 'right' },
      { hash: '0x99a352e9ce1013452c2baaeb7f9fc9ab327db2ed7b94e20e5400a60a16dd4033', position: 'right' },
      { hash: '0x5fb89b5a4178e42be579b6e6507892e5d201acd905a00ee7279fe17a90aa67ed', position: 'right' },
      { hash: '0x75d81c2dee584568334ba4f8a9f40fe4b1a8b5b6764e82e16a84b977b7e16b3f', position: 'right' },
      { hash: '0xc89dd19e4fdfe141da5fcdfed0ae3032c501824e7b65174fc3aca19668efa28f', position: 'right' },
      { hash: '0xb0e56ff9991fe5f4401abbb91949020f6f7e2debd708f051c592a68cd10cb183', position: 'right' },
    ], null, 2),
  };

  const handleVerify = async () => {
    setResult(null);
    setError('');

    // Parse merklePath
    let parsedPath;
    try {
      parsedPath = JSON.parse(merklePath);
      if (!Array.isArray(parsedPath)) throw new Error('Must be an array');
    } catch {
      setError(t('screens.integrity.errMerklePath'));
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/audit/integrity/verify`, {
        leafHash: leafHash.trim(),
        merklePath: parsedPath,
        rootHash: rootHash.trim(),
      });
      setResult(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('screens.integrity.errVerifyRequest'));
    } finally {
      setLoading(false);
    }
  };

  const loadExample = () => {
    if (!example) return;
    setLeafHash(example.leafHash);
    setMerklePath(example.merklePath);
    setRootHash(example.rootHash);
    setResult(null);
    setError('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Inter', sans-serif",
      color: S.textPrimary,
    }}>
      {/* Top bar */}
      <div style={{
        padding: '16px 32px',
        borderBottom: `1px solid ${S.borderLight}`,
        background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <img src="/Logo2.svg" alt="Regure" style={{ height: 28 }} />
        <span style={{ fontSize: 11, color: S.textMuted, fontWeight: 600, letterSpacing: 0.8 }}>
          {t('screens.integrity.verifyPageProtocolLabel')}
        </span>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px 60px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px', color: S.textPrimary }}>
            {t('screens.integrity.verifyPageTitle')}
          </h1>
          <p style={{ fontSize: 14, color: S.textSecondary, margin: '0 0 12px' }}>
            {t('screens.integrity.verifyPageSubtitle')}
          </p>
          <p style={{ fontSize: 12.5, color: S.textMuted, lineHeight: 1.6, maxWidth: 600, margin: '0 auto' }}>
            {t('screens.integrity.verifyPageIntro')}
          </p>
        </div>

        {/* Verification Form */}
        <div style={{
          background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14,
          padding: 28, marginBottom: 20,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary, marginBottom: 20 }}>
            {t('screens.integrity.verifyMerkleProof')}
          </div>

          <FormField label={t('screens.integrity.leafHashLabel')} sublabel={t('screens.integrity.leafHashSub')}>
            <input value={leafHash} onChange={e => setLeafHash(e.target.value)}
              placeholder="0x..."
              style={inputStyle} />
          </FormField>

          <FormField label={t('screens.integrity.merklePathLabel')} sublabel={t('screens.integrity.merklePathSub')}>
            <textarea value={merklePath} onChange={e => setMerklePath(e.target.value)}
              placeholder={t('screens.integrity.merklePathPlaceholder')}
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} />
          </FormField>

          <FormField label={t('screens.integrity.expectedRootLabel')} sublabel={t('screens.integrity.expectedRootSub')}>
            <input value={rootHash} onChange={e => setRootHash(e.target.value)}
              placeholder="0x..."
              style={inputStyle} />
          </FormField>

          {error && (
            <div style={{ fontSize: 12, color: S.danger, marginBottom: 12, padding: '8px 12px', background: '#fee2e2', borderRadius: 8 }}>
              {error}
            </div>
          )}

          <button onClick={handleVerify} disabled={loading || !leafHash || !merklePath || !rootHash}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px 20px',
              background: (!leafHash || !merklePath || !rootHash) ? '#94a3b8' : `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 600,
              cursor: (!leafHash || !merklePath || !rootHash) ? 'not-allowed' : 'pointer',
            }}>
            {loading ? (
              <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} /> {t('screens.integrity.verifyingProof')}</>
            ) : (
              <>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {t('screens.integrity.verifyProof')}
              </>
            )}
          </button>
        </div>

        {/* Example Helper */}
        <div style={{
          background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14,
          marginBottom: 20, overflow: 'hidden',
        }}>
          <div onClick={() => setExampleExpanded(e => !e)}
            style={{
              padding: '14px 20px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: S.textSecondary }}>
              {t('screens.integrity.tryExample')}
            </span>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={S.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: exampleExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {exampleExpanded && (
            <div style={{ padding: '0 20px 16px', borderTop: `1px solid ${S.borderLight}` }}>
              <div style={{ paddingTop: 14 }}>
                <p style={{ fontSize: 12, color: S.textMuted, margin: '0 0 10px', lineHeight: 1.5 }}>
                  {t('screens.integrity.exampleHint', { date: example.date })}
                </p>
                <button onClick={loadExample}
                  style={{
                    padding: '8px 16px', background: S.bgCard,
                    border: `1px solid ${S.borderLight}`, borderRadius: 8,
                    fontSize: 12, fontWeight: 600, color: S.burgundy, cursor: 'pointer',
                  }}>
                  {t('screens.integrity.loadExample')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        {result && (
          <div style={{
            background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14,
            overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{
              padding: '16px 20px',
              background: result.valid ? '#dcfce7' : '#fee2e2',
              borderBottom: `1px solid ${result.valid ? '#bbf7d0' : '#fecaca'}`,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              {result.valid ? (
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
              <span style={{ fontSize: 16, fontWeight: 700, color: result.valid ? '#15803d' : '#991b1b' }}>
                {result.valid ? t('screens.integrity.proofVerified') : t('screens.integrity.proofInvalid')}
              </span>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <HashRow label={t('screens.integrity.expectedRootHashRow')} hash={rootHash.trim()} color={result.valid ? '#15803d' : '#15803d'} />
              <div style={{ height: 8 }} />
              <HashRow label={t('screens.integrity.matchRow')} hash={result.valid ? t('screens.integrity.hashesMatch') : t('screens.integrity.hashesDoNotMatch')} color={result.valid ? '#15803d' : '#991b1b'} />
              <p style={{ fontSize: 12.5, color: S.textMuted, lineHeight: 1.6, margin: '14px 0 0' }}>
                {result.valid
                  ? t('screens.integrity.resultValidText')
                  : t('screens.integrity.resultInvalidText')}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 16 }}>
            <a href={`${BASE_URL?.replace('/api/v1', '')}/api/v1/openapi.yaml`} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: S.burgundy, fontWeight: 600, textDecoration: 'none' }}>
              {t('screens.integrity.openApiDocLink')}
            </a>
            <a href="#" style={{ fontSize: 12, color: S.burgundy, fontWeight: 600, textDecoration: 'none' }}>
              {t('screens.integrity.riaProtocolPaper')}
            </a>
          </div>
          <div style={{ fontSize: 11, color: S.textMuted }}>
            {t('screens.integrity.footerCredit')}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', fontSize: 13,
  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  border: `1px solid #e2e8f0`, borderRadius: 8,
  color: '#1e293b', background: '#fff', outline: 'none',
  boxSizing: 'border-box',
};

const FormField = ({ label, sublabel, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{label}</div>
    {sublabel && <div style={{ fontSize: 10.5, color: '#64748b', marginBottom: 6 }}>{sublabel}</div>}
    {children}
  </div>
);

const HashRow = ({ label, hash, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, minWidth: 140 }}>{label}</span>
    <code style={{ fontSize: 12, color: color || '#1e293b', fontFamily: "'SF Mono', 'Fira Code', monospace", wordBreak: 'break-all' }}>
      {hash}
    </code>
  </div>
);

export default VerifyAuditScreen;
